import * as React from "react";
import { Link, useNavigate } from "react-router";
import { useQuery } from "@tanstack/react-query";
import { BellRing } from "lucide-react";
import { useRealtime } from "@ozpanel/kit";
import { Button, sound, useAuth, useToast } from "@ozpanel/ui";
import { usePushEnable } from "../usePushEnable";

// WHAT AN ADMIN SEES OF AN ALERT WHILE THE CONSOLE IS OPEN — and what tells them when nothing else will.
//
// Measured in the node10 reboot drill, 2026-10-09: every alert of the day (rspamd stopped, a node down)
// was in the feed, the bell's count went up, and nothing else happened — no toast, no sound — while no
// admin device was subscribed to push, so nothing reached a phone either. An operator who was not staring
// at the bell saw none of it.
//
// So, while the console is open:
//   * NEW error/critical alerts from ANY node pop up as ONE toast (stays until dismissed) that lists the
//     latest and counts the rest, and the policy's sound plays. One toast, kept current — not one per
//     alert: node100 going down on 2026-10-10 raised 18 error/critical alerts in three minutes (the same
//     outage reported by both surviving nodes, module by module), and eighteen sticky toasts is a wall
//     nobody reads and nobody can clear. The full list is one click away, in the notifications screen. The source is the durable feed, not the live stream: the stream carries only the
//     node this console is on, the feed is replicated, and node11's outage must reach an admin signed in
//     to node100. The stream only makes it immediate. Alerts the gate suppressed are stored at warn and
//     do not pop up — the bell still has them, with the reason.
//   * while NO admin device receives push, a banner says so with a one-click enable — the gap the drill
//     found must be visible to the person who can close it, not only on a settings page.

type Priority = "info" | "warn" | "error" | "crit";
const rank: Record<string, number> = { info: 0, warn: 1, error: 2, crit: 3 };

interface FeedRow {
  id: number;
  module: string;
  kind: string;
  severity: Priority;
  /** The title: "node100: node11 not replicating PostgreSQL (2m24s)". */
  summary: string;
  /** One line: what it costs. */
  consequence?: string;
  /** One line: what to look at or do. */
  next?: string;
  node?: string;
  created_at: string;
  /** Closes something (a breach cleared, a module healthy again): never a new alarm, never a sound. */
  recovery?: boolean;
}

interface PushStatus {
  admin_devices: number;
  mine: number;
  note?: string;
}

export function LiveAlerts() {
  const { client, who } = useAuth();
  const { notify } = useToast();
  const navigate = useNavigate();
  // The alerts raised since the operator last dismissed the toast, newest first.
  const pending = React.useRef<FeedRow[]>([]);
  const superAdmin = who?.role === "super_admin";

  // Sound needs one real gesture to unlock (browser autoplay policy); arm it once.
  React.useEffect(() => {
    sound.armUnlock();
  }, []);

  const policy = useQuery({
    queryKey: ["alert-policy"],
    queryFn: () => client.rootFetch<{ sound_min_priority: Priority }>("/realtime/alerts/policy"),
    staleTime: 5 * 60_000,
  });

  const feed = useQuery({
    queryKey: ["live-alerts-feed"],
    queryFn: () => client.apiFetch<{ items: FeedRow[] }>("/me/notifications?limit=30"),
    refetchInterval: 15_000,
  });

  // The stream makes it immediate: any event on this node refetches the feed now.
  const { events } = useRealtime();
  const lastSeq = React.useRef<number | null>(null);
  React.useEffect(() => {
    const top = events[0]?.seq ?? null;
    if (top !== null && top !== lastSeq.current) {
      lastSeq.current = top;
      void feed.refetch();
    }
  }, [events, feed]);

  // Only rows that arrive AFTER the console opened: the highest id seen on the first read is the floor.
  const floor = React.useRef<number | null>(null);
  React.useEffect(() => {
    const rows = feed.data?.items;
    if (!rows) return;
    const maxID = rows.reduce((m, r) => Math.max(m, r.id), 0);
    if (floor.current === null) {
      floor.current = maxID;
      return;
    }
    const fresh = rows.filter((r) => r.id > (floor.current ?? 0)).sort((a, b) => a.id - b.id);
    floor.current = Math.max(floor.current, maxID);
    const soundMin = rank[policy.data?.sound_min_priority ?? "error"] ?? 2;
    let played = false;
    let added = false;
    for (const r of fresh) {
      if (r.recovery) continue;
      const sev = rank[r.severity] ?? 0;
      if (sev >= 2) {
        pending.current.unshift(r);
        added = true;
      }
      if (!played && sev >= soundMin) {
        sound.play("default"); // never throws; silent until a gesture unlocked audio
        played = true;
      }
    }
    if (!added) return;
    const all = pending.current;
    const crit = all.filter((r) => r.severity === "crit").length;
    // The summary is the title and already names the node; a long one (an emitter still writing a
    // paragraph) is cut — the full text is in the notifications screen.
    const short = (s: string) => (s.length > 120 ? s.slice(0, 119) + "…" : s);
    const line = (r: FeedRow) => `${r.severity === "crit" ? "CRITICAL" : "Error"} · ${short(r.summary)}`;
    const one = all.length === 1 ? all[0] : null;
    notify({
      key: "live-alerts",
      kind: "error",
      title: one
        ? `${one.severity === "crit" ? "Critical" : "Error"} · ${short(one.summary)}`
        : `${all.length} new alerts${crit ? ` — ${crit} critical` : ""}`,
      body: one
        ? // Two short lines — what it costs and what to do. The explanation stays behind "details".
          [one.consequence, one.next ? `Next: ${one.next}` : ""].filter(Boolean).join("\n") || one.module
        : // Critical first, then newest; four lines, the rest counted.
          [...all]
            .sort((a, b) => (rank[b.severity] ?? 0) - (rank[a.severity] ?? 0) || b.id - a.id)
            .slice(0, 4)
            .map(line)
            .join("\n") + (all.length > 4 ? `\n… and ${all.length - 4} more` : ""),
      action: { label: "Open notifications", onClick: () => navigate("/notifications") },
      onDismiss: () => {
        pending.current = [];
      },
    });
  }, [feed.data, notify, policy.data, navigate]);

  const push = useQuery({
    queryKey: ["notify-push-status"],
    queryFn: () => client.apiFetch<PushStatus>("/notify/push/status"),
    enabled: superAdmin,
    refetchInterval: 5 * 60_000,
  });
  const { state, busy, enable } = usePushEnable();
  const [dismissed, setDismissed] = React.useState(() => {
    try {
      return sessionStorage.getItem("ozp-push-banner-dismissed") === "1";
    } catch {
      return false;
    }
  });

  if (!superAdmin || dismissed || !push.data || push.data.admin_devices > 0) return null;
  return (
    <div className="flex flex-wrap items-center gap-3 border-b border-[var(--ozp-warn-line,#b45309)] bg-[var(--ozp-warn-soft,#451a03)] px-4 py-2 text-[12.5px] text-fg">
      <BellRing size={15} className="shrink-0 text-warn" />
      <span className="min-w-0 flex-1">
        <b>No admin device receives alerts.</b> Alerts reach only a console that is open — a node going down
        at night pages nobody.{" "}
        {state === "denied"
          ? "This browser has blocked notifications for this site; allow them in its site settings, or enable push on another device."
          : state === "unsupported"
            ? "This browser cannot receive push; enable it on another device."
            : "Enable push on a device you carry."}{" "}
        <Link to="/notifications" className="underline">
          Notifications
        </Link>
      </span>
      {state !== "denied" && state !== "unsupported" && (
        <Button size="sm" variant="accent" disabled={busy} onClick={() => void enable()}>
          {busy ? "Enabling…" : "Enable on this browser"}
        </Button>
      )}
      <Button
        size="sm"
        variant="ghost"
        onClick={() => {
          setDismissed(true);
          try {
            sessionStorage.setItem("ozp-push-banner-dismissed", "1");
          } catch {
            /* a banner that comes back next session is the safe failure */
          }
        }}
      >
        Not now
      </Button>
    </div>
  );
}
